import React, { useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, graphConfig } from './authConfig';
import { Document, Packer, Paragraph, TextRun, AlignmentType, UnderlineType, LevelFormat } from 'docx';
import { saveAs } from 'file-saver';

// ============================================================================
// THEME & STYLING
// ============================================================================

const colors = {
  primary: '#1a5f9e', primaryDark: '#0d3a5c', primaryLight: '#2a7ac4',
  accent: '#4dabf5', dna: '#5bc4e8', background: '#0a1929',
  surface: '#132f4c', surfaceLight: '#1e4976', text: '#ffffff',
  textSecondary: '#b4c7dc', success: '#4caf50', warning: '#ff9800', error: '#f44336',
  hr: '#9c27b0', // Purple accent for HR
  onboarding: '#00bcd4', // Cyan for onboarding
  offboarding: '#ff5722', // Deep orange for offboarding
};

// ============================================================================
// CONFIGURATION
// ============================================================================

const SHAREPOINT_CONFIG = {
  siteId: 'a24d8c46-4506-41c7-922f-746c5722bc90',
  lists: {
    requisitions: 'CONNECT_Requisitions',
    onboarding: 'CONNECT_Onboarding',
    offboarding: 'CONNECT_Offboarding',
    settings: 'CONNECT_Settings',
    users: 'CONNECT_Users',
  },
  driveId: null,
};

const TEAMS_CONFIG = {
  teamId: '47fe5c97-fb2d-4ac0-bcc7-625ed42019d0',
  channelId: '19:myzFxzNyccoxvZXgenySsFhlLM8NalT3cHgQSVTSf481@thread.tacv2',
};

const EMAIL_CONFIG = {
  sharedMailbox: 'itdepartment@OutlookTherapeutics.com',
  appUrl: 'https://connect-hr.outlooktherapeutics.com',
};

// Default notification recipients
const DEFAULT_RECIPIENTS = [
  { name: 'Christoph Puetz', email: 'christophpuetz@outlooktherapeutics.com', active: true },
  { name: 'Markus Muehlemann', email: 'markusmuehlemann@outlooktherapeutics.com', active: true },
  { name: 'Entisar Dahan', email: 'entisardahan@outlooktherapeutics.com', active: true },
  { name: 'Lisa Hinshaw', email: 'lisahinshaw@outlooktherapeutics.com', active: true },
  { name: 'IT Support', email: 'support@outlooktherapeutics.com', active: true },
  { name: 'Danielle Greene', email: 'daniellegreene@outlooktherapeutics.com', active: true },
];

// Equipment return locations
const EQUIPMENT_RETURN_LOCATIONS = [
  { id: 'us', label: 'US: Noesis Group Inc., 2782 Gateway Rd, Carlsbad, CA 92009' },
  { id: 'eu', label: 'EU/UK: Markus Muehlemann, Eichmatt 6, 6343 Rotkreuz / Switzerland' },
  { id: 'other', label: 'Other (specify below)' },
];

// Mailbox handling options
const MAILBOX_OPTIONS = [
  { id: 'access', label: 'Access to the user mailbox after departure' },
  { id: 'ooo', label: 'Set "Out of Office" message' },
  { id: 'shared', label: 'Convert user mailbox to shared mailbox and provide access to specified users' },
  { id: 'other', label: 'Other' },
];

// ============================================================================
// AUTHENTICATION HOOK
// ============================================================================

const useCurrentUser = () => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAccessToken = useCallback(async (scopes = loginRequest.scopes) => {
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({ scopes, account });
      return response.accessToken;
    } catch (error) {
      if (error.name === 'InteractionRequiredAuthError') {
        const response = await instance.acquireTokenPopup({ scopes, account });
        return response.accessToken;
      }
      throw error;
    }
  }, [instance, account]);

  useEffect(() => {
    const loadUser = async () => {
      if (!account || inProgress !== InteractionStatus.None) return;
      try {
        const token = await getAccessToken([...loginRequest.scopes, 'User.Read']);
        const response = await fetch(graphConfig.graphMeEndpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        setUser({ name: data.displayName, email: data.mail || data.userPrincipalName, id: data.id });
      } catch (error) { console.error('Error loading user:', error); }
      finally { setLoading(false); }
    };
    loadUser();
  }, [account, inProgress, getAccessToken]);

  return { user, loading, getAccessToken };
};

// ============================================================================
// SHAREPOINT SERVICE
// ============================================================================

const graphRequest = async (url, accessToken, options = {}) => {
  const response = await fetch(url, {
    headers: { 
      Authorization: `Bearer ${accessToken}`, 
      'Content-Type': 'application/json',
      'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly',
      ...options.headers 
    },
    ...options,
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.error('Graph API Error:', error);
    throw new Error(error.error?.message || `Request failed: ${response.status}`);
  }
  return options.method === 'DELETE' ? null : response.json();
};

const getListUrl = (listName) => `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_CONFIG.siteId}/lists/${listName}`;

// Check if user is admin
const checkIsAdmin = async (accessToken, userEmail) => {
  try {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.users)}/items?$expand=fields&$filter=fields/Email eq '${userEmail}'`;
    const data = await graphRequest(url, accessToken);
    if (data.value.length > 0) {
      return data.value[0].fields.IsAdmin === true;
    }
    return false;
  } catch (err) {
    console.error('Error checking admin status:', err);
    return false;
  }
};

// Load approved requisitions
const loadApprovedRequisitions = async (accessToken) => {
  const url = `${getListUrl(SHAREPOINT_CONFIG.lists.requisitions)}/items?$expand=fields&$filter=fields/Status eq 'Approved' or fields/Status eq 'Recruiting' or fields/Status eq 'Interviewing' or fields/Status eq 'Verbal Offer' or fields/Status eq 'Offer Letter in Progress'&$orderby=fields/Created desc`;
  const data = await graphRequest(url, accessToken);
  return data.value.map(item => ({
    id: item.fields.RequisitionID,
    spItemId: item.id,
    positionTitle: item.fields.PositionTitle,
    department: item.fields.Department,
    location: item.fields.Location_x002f_Regionfield,
    roleType: item.fields.RoleType,
    requestorName: item.fields.RequestorName,
    status: item.fields.Status,
    projectedStartDate: item.fields.ProjectedStartDate,
    // Negotiated terms from recruiter
    negotiatedSalary: item.fields.NegotiatedSalary,
    negotiatedStockOptions: item.fields.NegotiatedStockOptions,
    negotiatedBonus: item.fields.NegotiatedBonus,
    negotiatedVacationDays: item.fields.NegotiatedVacationDays,
    negotiatedOtherCompBonus: item.fields.NegotiatedOtherCompBonus,
    hiringManagerName: item.fields.RequestorName,
  }));
};

// Load onboarding records
const loadOnboardingRecords = async (accessToken) => {
  const url = `${getListUrl(SHAREPOINT_CONFIG.lists.onboarding)}/items?$expand=fields&$orderby=fields/Created desc`;
  const data = await graphRequest(url, accessToken);
  return data.value.map(item => ({
    id: item.id,
    onboardingId: item.fields.Title,
    linkedRequisitionId: item.fields.RequisitionID, // Using existing column name
    firstName: item.fields.FirstName,
    lastName: item.fields.LastName,
    personalEmail: item.fields.PersonalEmail,
    jobTitle: item.fields.JobTitle,
    department: item.fields.Department,
    hiringManagerEmail: item.fields.ManagerEmail, // Only email, no separate name field
    startDate: item.fields.StartDate,
    workLocation: item.fields.WorkLocation,
    roleType: item.fields.RoleType,
    // Address fields
    streetAddress: item.fields.StreetAddress || '',
    zipCity: item.fields.ZipCity || '',
    country: item.fields.Country || '',
    // Employee type
    employeeType: item.fields.EmployeeType || 'Employee',
    // Software access - using existing Yes/No columns
    needsDocusign: item.fields.NeedsDocusign || false,
    needsNetsuite: item.fields.NeedsNetsuite || false,
    needsAdobePro: item.fields.NeedsAdobePro || false,
    sharePointAccess: item.fields.SharePointAccess || '',
    shareFileAccess: item.fields.ShareFileAccess || '',
    notes: item.fields.Notes,
    status: item.fields.ITStatus || 'Pending', // Using existing column name
    submittedBy: item.fields.SubmittedBy,
    submittedAt: item.fields.SubmittedAt,
  }));
};

// Load offboarding records
const loadOffboardingRecords = async (accessToken) => {
  try {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.offboarding)}/items?$expand=fields&$orderby=fields/Created desc`;
    const data = await graphRequest(url, accessToken);
    return data.value.map(item => ({
      id: item.id,
      offboardingId: item.fields.Title,
      employeeEmail: item.fields.EmployeeEmail,
      employeeName: item.fields.EmployeeName,
      isUrgent: item.fields.IsUrgent,
      lastDay: item.fields.LastDay,
      removeAccessDate: item.fields.RemoveAccessDate,
      equipmentReturnLocation: item.fields.EquipmentReturnLocation,
      equipmentReturnOther: item.fields.EquipmentReturnOther,
      mailboxOptions: item.fields.MailboxOptions ? JSON.parse(item.fields.MailboxOptions) : [],
      oooMessage: item.fields.OOOMessage,
      sharedMailboxUsers: item.fields.SharedMailboxUsers,
      otherMailboxNotes: item.fields.OtherMailboxNotes,
      notes: item.fields.Notes,
      status: item.fields.Status || 'Pending',
      submittedBy: item.fields.SubmittedBy,
      submittedAt: item.fields.SubmittedAt,
    }));
  } catch (err) {
    console.error('Error loading offboarding records:', err);
    return [];
  }
};

// Load settings
const loadSettings = async (accessToken) => {
  try {
    const url = `${getListUrl(SHAREPOINT_CONFIG.lists.settings)}/items?$expand=fields`;
    const data = await graphRequest(url, accessToken);
    const settings = {};
    data.value.forEach(item => {
      settings[item.fields.Title] = {
        id: item.id,
        value: item.fields.Value ? JSON.parse(item.fields.Value) : null,
        isActive: item.fields.IsActive,
      };
    });
    return settings;
  } catch (err) {
    console.error('Error loading settings:', err);
    return {};
  }
};

// Generate next ID
const generateId = async (accessToken, prefix, listName) => {
  const year = new Date().getFullYear();
  const url = `${getListUrl(listName)}/items?$expand=fields&$filter=startswith(fields/Title, '${prefix}-${year}')&$orderby=fields/Title desc&$top=1`;
  try {
    const data = await graphRequest(url, accessToken);
    if (data.value.length > 0) {
      const lastId = data.value[0].fields.Title;
      const lastNum = parseInt(lastId.split('-')[2]) || 0;
      return `${prefix}-${year}-${String(lastNum + 1).padStart(3, '0')}`;
    }
  } catch (err) {
    console.error('Error generating ID:', err);
  }
  return `${prefix}-${year}-001`;
};

// Create onboarding record
const createOnboardingRecord = async (accessToken, data, user) => {
  const onboardingId = await generateId(accessToken, 'ONB', SHAREPOINT_CONFIG.lists.onboarding);
  const url = `${getListUrl(SHAREPOINT_CONFIG.lists.onboarding)}/items`;
  const body = {
    fields: {
      Title: onboardingId,
      RequisitionID: data.linkedRequisitionId || '', // Using existing column name
      FirstName: data.firstName,
      LastName: data.lastName,
      PersonalEmail: data.personalEmail || '',
      JobTitle: data.jobTitle,
      Department: data.department,
      ManagerEmail: data.hiringManagerEmail || '', // Manager email (no separate name field)
      StartDate: data.startDate,
      WorkLocation: data.workLocation || '',
      RoleType: data.roleType || 'Remote',
      // Address fields
      StreetAddress: data.streetAddress || '',
      ZipCity: data.zipCity || '',
      Country: data.country || '',
      // Employee type
      EmployeeType: data.employeeType || 'Employee',
      // Software access - using existing Yes/No columns
      NeedsDocusign: data.needsDocusign || false,
      NeedsNetsuite: data.needsNetsuite || false,
      NeedsAdobePro: data.needsAdobePro || false,
      SharePointAccess: data.sharePointAccess || '',
      ShareFileAccess: data.shareFileAccess || '',
      Notes: data.notes || '',
      ITStatus: 'Pending', // Using existing column name
      SubmittedBy: user.name,
      SubmittedAt: new Date().toISOString(),
    },
  };
  await graphRequest(url, accessToken, { method: 'POST', body: JSON.stringify(body) });
  return onboardingId;
};

// Create offboarding record
const createOffboardingRecord = async (accessToken, data, user) => {
  const offboardingId = await generateId(accessToken, 'OFF', SHAREPOINT_CONFIG.lists.offboarding);
  const url = `${getListUrl(SHAREPOINT_CONFIG.lists.offboarding)}/items`;
  const body = {
    fields: {
      Title: offboardingId,
      EmployeeEmail: data.employeeEmail,
      EmployeeName: data.employeeName || '',
      IsUrgent: data.isUrgent,
      LastDay: data.lastDay,
      RemoveAccessDate: data.removeAccessDate,
      EquipmentReturnLocation: data.equipmentReturnLocation,
      EquipmentReturnOther: data.equipmentReturnOther || '',
      MailboxOptions: JSON.stringify(data.mailboxOptions || []),
      OOOMessage: data.oooMessage || '',
      SharedMailboxUsers: data.sharedMailboxUsers || '',
      OtherMailboxNotes: data.otherMailboxNotes || '',
      Notes: data.notes || '',
      Status: 'Pending',
      SubmittedBy: user.name,
      SubmittedAt: new Date().toISOString(),
    },
  };
  await graphRequest(url, accessToken, { method: 'POST', body: JSON.stringify(body) });
  return offboardingId;
};

// Save settings
const saveSettings = async (accessToken, settingKey, value, user) => {
  // First, try to find existing setting
  const url = `${getListUrl(SHAREPOINT_CONFIG.lists.settings)}/items?$expand=fields&$filter=fields/Title eq '${settingKey}'`;
  const data = await graphRequest(url, accessToken);
  
  const body = {
    fields: {
      Title: settingKey,
      Value: JSON.stringify(value),
      IsActive: true,
      UpdatedBy: user.name,
      UpdatedAt: new Date().toISOString(),
    },
  };

  if (data.value.length > 0) {
    // Update existing
    const updateUrl = `${getListUrl(SHAREPOINT_CONFIG.lists.settings)}/items/${data.value[0].id}`;
    await graphRequest(updateUrl, accessToken, { method: 'PATCH', body: JSON.stringify(body) });
  } else {
    // Create new
    const createUrl = `${getListUrl(SHAREPOINT_CONFIG.lists.settings)}/items`;
    await graphRequest(createUrl, accessToken, { method: 'POST', body: JSON.stringify(body) });
  }
};

// Get drive ID for attachments
const getDriveId = async (accessToken) => {
  if (SHAREPOINT_CONFIG.driveId) return SHAREPOINT_CONFIG.driveId;
  const url = `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_CONFIG.siteId}/drives`;
  const data = await graphRequest(url, accessToken);
  const attachmentsDrive = data.value.find(d => d.name === 'CONNECT_Attachments');
  if (attachmentsDrive) {
    SHAREPOINT_CONFIG.driveId = attachmentsDrive.id;
    return attachmentsDrive.id;
  }
  throw new Error('CONNECT_Attachments document library not found');
};

// Upload offer letter to SharePoint
const uploadOfferLetter = async (accessToken, requisitionId, fileName, fileBuffer) => {
  const driveId = await getDriveId(accessToken);
  const folderPath = `OfferLetters/${requisitionId}`;
  const url = `https://graph.microsoft.com/v1.0/drives/${driveId}/root:/${folderPath}/${fileName}:/content`;
  
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    },
    body: fileBuffer,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to upload file');
  }
  
  return response.json();
};

// ============================================================================
// EMAIL & TEAMS NOTIFICATION SERVICE
// ============================================================================

const sendEmailNotification = async (accessToken, recipients, subject, htmlBody, fromSharedMailbox = true) => {
  // With delegated permissions, we can either:
  // 1. Send as the signed-in user: /me/sendMail
  // 2. Send from shared mailbox: /users/{shared-mailbox}/sendMail (requires "Send As" permission in Exchange)
  
  // Try shared mailbox first, fall back to user's mailbox
  let url = fromSharedMailbox 
    ? `https://graph.microsoft.com/v1.0/users/${EMAIL_CONFIG.sharedMailbox}/sendMail`
    : 'https://graph.microsoft.com/v1.0/me/sendMail';
  
  const message = {
    message: {
      subject: subject,
      body: {
        contentType: 'HTML',
        content: htmlBody,
      },
      toRecipients: recipients.map(r => ({
        emailAddress: { address: r.email, name: r.name || r.email }
      })),
    },
    saveToSentItems: true,
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(message),
    });
    
    if (!response.ok) {
      // If shared mailbox fails, try sending from user's mailbox
      if (fromSharedMailbox) {
        console.log('[HR] Shared mailbox failed, trying user mailbox...');
        return sendEmailNotification(accessToken, recipients, subject, htmlBody, false);
      }
      throw new Error(`Email send failed: ${response.status}`);
    }
    
    console.log('[HR] Email sent successfully from', fromSharedMailbox ? 'shared mailbox' : 'user mailbox');
    return true;
  } catch (err) {
    console.error('[HR] Error sending email:', err);
    // If shared mailbox fails, try user's mailbox
    if (fromSharedMailbox) {
      console.log('[HR] Retrying with user mailbox...');
      return sendEmailNotification(accessToken, recipients, subject, htmlBody, false);
    }
    return false;
  }
};

const postToTeamsChannel = async (accessToken, subject, body) => {
  const url = `https://graph.microsoft.com/v1.0/teams/${TEAMS_CONFIG.teamId}/channels/${TEAMS_CONFIG.channelId}/messages`;
  
  const message = {
    subject: subject,
    body: {
      contentType: 'html',
      content: body,
    },
  };

  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(message),
    });
    console.log('[HR] Teams message posted successfully');
    return true;
  } catch (err) {
    console.error('[HR] Error posting to Teams:', err);
    return false;
  }
};

// Build onboarding notification email
const buildOnboardingEmail = (data, requestId) => {
  // Build software needs list
  const softwareNeeds = [];
  if (data.needsDocusign) softwareNeeds.push('DocuSign');
  if (data.needsNetsuite) softwareNeeds.push('NetSuite');
  if (data.needsAdobePro) softwareNeeds.push('Adobe Pro');
  
  // Derive manager name from email if possible (firstnamelastname@domain.com -> Firstname Lastname)
  const managerDisplay = data.hiringManager || data.hiringManagerEmail || 'Not specified';
  
  // Build address string
  const addressParts = [data.streetAddress, data.zipCity, data.country].filter(Boolean);
  const addressDisplay = addressParts.length > 0 ? addressParts.join(', ') : 'Not specified';
  
  return `
    <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: #00bcd4; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
        <h2 style="margin: 0;">🚀 New Onboarding Request: ${requestId}</h2>
      </div>
      <div style="background: #f5f5f5; padding: 20px; border-radius: 0 0 8px 8px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Employee Name:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.firstName} ${data.lastName}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Employee Type:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.employeeType || 'Employee'}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Job Title:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.jobTitle}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Department:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.department}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Start Date:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${new Date(data.startDate).toLocaleDateString()}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Hiring Manager:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${managerDisplay}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Address:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${addressDisplay}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Work Location:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.workLocation || 'Not specified'}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Role Type:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.roleType}</td></tr>
          ${data.personalEmail ? `<tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Personal Email:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.personalEmail}</td></tr>` : ''}
          ${softwareNeeds.length > 0 ? `<tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Software Needed:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${softwareNeeds.join(', ')}</td></tr>` : ''}
          ${data.sharePointAccess ? `<tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>SharePoint Access:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.sharePointAccess}</td></tr>` : ''}
          ${data.shareFileAccess ? `<tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>ShareFile Access:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.shareFileAccess}</td></tr>` : ''}
          ${data.notes ? `<tr><td style="padding: 8px 0;"><strong>Notes:</strong></td><td style="padding: 8px 0;">${data.notes}</td></tr>` : ''}
        </table>
        <div style="margin-top: 20px; padding-top: 16px; border-top: 1px solid #ddd;">
          <a href="${EMAIL_CONFIG.appUrl}" style="display: inline-block; padding: 10px 20px; background: #00bcd4; color: white; text-decoration: none; border-radius: 6px; font-weight: 500;">View in CONNECT HR</a>
        </div>
        <p style="margin-top: 16px; font-size: 12px; color: #666;">This is an automated notification from the CONNECT HR system.</p>
      </div>
    </div>
  `;
};

// Build offboarding notification email
const buildOffboardingEmail = (data, requestId) => {
  const urgentBadge = data.isUrgent ? '<span style="background: #f44336; color: white; padding: 2px 8px; border-radius: 4px; font-size: 12px; margin-left: 10px;">URGENT</span>' : '';
  const locationLabel = EQUIPMENT_RETURN_LOCATIONS.find(l => l.id === data.equipmentReturnLocation)?.label || data.equipmentReturnLocation;
  
  return `
    <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: #ff5722; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
        <h2 style="margin: 0;">👋 Offboarding Request: ${requestId} ${urgentBadge}</h2>
      </div>
      <div style="background: #f5f5f5; padding: 20px; border-radius: 0 0 8px 8px;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Employee Email:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.employeeEmail}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Last Day:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${new Date(data.lastDay).toLocaleDateString()}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Remove Access Date:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${new Date(data.removeAccessDate).toLocaleDateString()}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Equipment Return:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${locationLabel}${data.equipmentReturnOther ? ` - ${data.equipmentReturnOther}` : ''}</td></tr>
          <tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Mailbox Options:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.mailboxOptions.map(o => MAILBOX_OPTIONS.find(m => m.id === o)?.label || o).join(', ') || 'None'}</td></tr>
          ${data.oooMessage ? `<tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>OOO Message:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.oooMessage}</td></tr>` : ''}
          ${data.sharedMailboxUsers ? `<tr><td style="padding: 8px 0; border-bottom: 1px solid #ddd;"><strong>Shared Mailbox Users:</strong></td><td style="padding: 8px 0; border-bottom: 1px solid #ddd;">${data.sharedMailboxUsers}</td></tr>` : ''}
          ${data.notes ? `<tr><td style="padding: 8px 0;"><strong>Notes:</strong></td><td style="padding: 8px 0;">${data.notes}</td></tr>` : ''}
        </table>
        <div style="margin-top: 20px; padding-top: 16px; border-top: 1px solid #ddd;">
          <a href="${EMAIL_CONFIG.appUrl}" style="display: inline-block; padding: 10px 20px; background: #ff5722; color: white; text-decoration: none; border-radius: 6px; font-weight: 500;">View in CONNECT HR</a>
        </div>
        <p style="margin-top: 16px; font-size: 12px; color: #666;">This is an automated notification from the CONNECT HR system.</p>
      </div>
    </div>
  `;
};

// ============================================================================
// OFFER LETTER GENERATOR
// ============================================================================

const generateOfferLetter = async (data) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const children = [
    // Date
    new Paragraph({
      children: [new TextRun({ text: formatDate(data.offerDate), bold: true })],
      spacing: { after: 200 },
    }),
    
    // Candidate Name
    new Paragraph({
      children: [new TextRun({ text: `${data.firstName} ${data.lastName}`, bold: true })],
      spacing: { after: 400 },
    }),
    
    // Salutation
    new Paragraph({
      children: [
        new TextRun({ text: 'Dear ' }),
        new TextRun({ text: data.firstName, bold: true }),
        new TextRun({ text: ':' }),
      ],
      spacing: { after: 200 },
    }),
    
    // Opening paragraph
    new Paragraph({
      children: [
        new TextRun({ text: "On behalf of the Outlook Therapeutics' Senior Management Team, I am pleased to extend this job offer to you to join us on " }),
        new TextRun({ text: formatDate(data.startDate), bold: true }),
        new TextRun({ text: '. The following represents our offer to you to join the Company:' }),
      ],
      spacing: { after: 200 },
    }),
    
    // Position
    new Paragraph({
      children: [
        new TextRun({ text: 'Position: ', bold: true }),
        new TextRun({ text: data.jobTitle, bold: true }),
        new TextRun({ text: '. Please refer to the attached job description that will set expectations for this job. This is a ' }),
        new TextRun({ text: data.roleType?.toLowerCase() || 'remote' }),
        new TextRun({ text: ' position.' }),
      ],
      spacing: { after: 200 },
    }),
    
    // Location
    new Paragraph({
      children: [
        new TextRun({ text: 'Location/Facility: ', bold: true }),
        new TextRun({ text: data.workLocation || '111 S. Wood Ave Unit #100 Iselin, NJ 08830' }),
      ],
      spacing: { after: 200 },
    }),
    
    // Base Pay
    new Paragraph({
      children: [
        new TextRun({ text: 'Base Pay: ', bold: true }),
        new TextRun({ text: 'Your annual base pay will be ' }),
        new TextRun({ text: formatCurrency(data.baseSalary), bold: true }),
        new TextRun({ text: ' and will be paid on a bi-weekly basis.' }),
      ],
      spacing: { after: 200 },
    }),
    
    // Incentive Pay
    new Paragraph({
      children: [
        new TextRun({ text: 'Incentive Pay: ', bold: true }),
        new TextRun({ text: 'You will participate in the success of the company and would be rewarded based on company performance and ' }),
        new TextRun({ text: 'your performance', underline: { type: UnderlineType.SINGLE } }),
        new TextRun({ text: '. Subject to meeting the company target and a fully performing evaluation, your bonus target shall be ' }),
        new TextRun({ text: `${data.bonusPercentage}%`, bold: true }),
        new TextRun({ text: ' of the base pay. The year\'s incentive pay accounting starts on January 1st, ' }),
        new TextRun({ text: data.bonusYear || new Date().getFullYear().toString(), bold: true }),
        new TextRun({ text: ' and will be prorated based on your start date.' }),
      ],
      spacing: { after: 200 },
    }),
    
    // Stock Options
    new Paragraph({
      children: [
        new TextRun({ text: 'Stock Options: ', bold: true }),
        new TextRun({ text: "Pending approval by the Company's Board of Directors or its Compensation Committee and as a material inducement to your acceptance of this offer and entering into employment with the Company, following your start date, you will receive an option to purchase " }),
        new TextRun({ text: data.stockOptions?.toLocaleString() || '0', bold: true }),
        new TextRun({ text: " shares of the Company's common stock, with an exercise price equal to the fair market value of the common stock on the date of grant. This award will be made pursuant to, and subject to the terms and conditions of, an equity incentive plan maintained by the Company and a stock option agreement thereunder, including vesting requirements." }),
      ],
      spacing: { after: 200 },
    }),
    
    // Vacation
    new Paragraph({
      children: [
        new TextRun({ text: 'Vacation: ', bold: true }),
        new TextRun({ text: 'You are eligible for ' }),
        new TextRun({ text: data.vacationWeeks?.toString() || '3', bold: true }),
        new TextRun({ text: " weeks' vacation along with typical holidays in the calendar year." }),
      ],
      spacing: { after: 200 },
    }),
  ];

  // Add Other Comp/Bonus section if provided (placeholder for future verbiage)
  if (data.otherCompBonus) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Additional Compensation: ', bold: true }),
          new TextRun({ text: data.otherCompBonus }),
        ],
        spacing: { after: 200 },
      })
    );
  }

  // Continue with remaining sections
  children.push(
    new Paragraph({
      children: [new TextRun({ text: 'This will be detailed in the employee handbook.' })],
      spacing: { after: 200 },
    }),
    
    // Employee Benefits
    new Paragraph({
      children: [new TextRun({ text: 'Employee Benefits Program:', bold: true })],
      spacing: { after: 100 },
    }),
    
    new Paragraph({
      numbering: { reference: 'benefits-list', level: 0 },
      children: [new TextRun({ text: 'Medical Health Care Plan' })],
    }),
    
    new Paragraph({
      numbering: { reference: 'benefits-list', level: 0 },
      children: [new TextRun({ text: 'Employee Insurance Plan: Company will offer employee insurance plan.' })],
    }),
    
    new Paragraph({
      numbering: { reference: 'benefits-list', level: 0 },
      children: [new TextRun({ text: '401K: The Company will match contributions up to 3% (dollar per dollar) of your base pay as contribution to the 401K plan subject to your vesting in the plan.' })],
      spacing: { after: 200 },
    }),
    
    // IP Protection
    new Paragraph({
      children: [
        new TextRun({ text: 'Intellectual Property Protection: ', bold: true }),
        new TextRun({ text: 'The Company requires you not to disclose any proprietary information from your previous employer/s. The company requires you to keep confidential any propriety information that you will come across in this employment.' }),
      ],
      spacing: { after: 200 },
    }),
    
    // At-Will
    new Paragraph({
      children: [
        new TextRun({ text: 'Employment At-Will: ', bold: true }),
        new TextRun({ text: 'At all times during your employment with the Company, you will be an employee at-will. In other words, Outlook Therapeutics Inc. has the right to terminate your employment with or without cause at any time and you have a right to terminate your employment with Outlook Therapeutics Inc. at any time' }),
      ],
      spacing: { after: 200 },
    }),
    
    // Closing
    new Paragraph({
      children: [
        new TextRun({ text: 'On behalf of the Senior Management Team, I look forward to your joining Outlook Therapeutics Inc. and hope that you will find this offer satisfactory in every respect. Per Company policy, this offer is contingent upon successful completion of a background check. This offer should be signed by ' }),
        new TextRun({ text: formatDate(data.signByDate), bold: true }),
        new TextRun({ text: ', and one copy returned to the Company.' }),
      ],
      spacing: { after: 400 },
    }),
    
    new Paragraph({
      children: [new TextRun({ text: 'Sincerely,' })],
      spacing: { after: 400 },
    }),
    
    // Signature lines
    new Paragraph({
      children: [new TextRun({ text: '_____________________     _____________________' })],
      spacing: { after: 100 },
    }),
    
    new Paragraph({
      children: [
        new TextRun({ text: data.hiringManagerName, bold: true }),
        new TextRun({ text: '     Date' }),
      ],
      spacing: { after: 100 },
    }),
    
    new Paragraph({
      children: [new TextRun({ text: data.hiringManagerTitle, bold: true })],
      spacing: { after: 400 },
    }),
    
    // Acceptance
    new Paragraph({
      children: [new TextRun({ text: 'I accept this offer with the terms and conditions as outlined in this letter:' })],
      spacing: { after: 400 },
    }),
    
    new Paragraph({
      children: [new TextRun({ text: '__________________________     _____________________' })],
      spacing: { after: 100 },
    }),
    
    new Paragraph({
      children: [
        new TextRun({ text: `${data.firstName} ${data.lastName}`, bold: true }),
        new TextRun({ text: '     Date' }),
      ],
    }),
  );

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: 'Times New Roman', size: 24 },
        },
      },
    },
    numbering: {
      config: [
        {
          reference: 'benefits-list',
          levels: [{
            level: 0,
            format: LevelFormat.LOWER_ROMAN,
            text: '%1)',
            alignment: AlignmentType.LEFT,
            style: { paragraph: { indent: { left: 720, hanging: 360 } } },
          }],
        },
      ],
    },
    sections: [{
      properties: {
        page: {
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      children: children,
    }],
  });

  return Packer.toBlob(doc);
};

// ============================================================================
// UI COMPONENTS
// ============================================================================

const LoadingScreen = ({ message }) => (
  <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${colors.background} 0%, ${colors.primaryDark} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Segoe UI', sans-serif" }}>
    <div style={{ textAlign: 'center', color: colors.text }}>
      <div style={{ width: '50px', height: '50px', border: `3px solid ${colors.surfaceLight}`, borderTopColor: colors.hr, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }} />
      <div style={{ fontSize: '16px', opacity: 0.9 }}>{message || 'Loading...'}</div>
    </div>
  </div>
);

const LoginScreen = () => {
  const { instance } = useMsal();
  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${colors.background} 0%, ${colors.primaryDark} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ width: '80px', height: '80px', background: colors.hr, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '32px', fontWeight: 700, color: colors.text }}>HR</div>
        <h1 style={{ color: colors.text, fontSize: '28px', margin: '0 0 8px' }}>CONNECT HR</h1>
        <p style={{ color: colors.textSecondary, margin: '0 0 32px' }}>HR Management Dashboard</p>
        <button onClick={() => instance.loginRedirect(loginRequest)} style={{ padding: '14px 32px', background: colors.hr, color: colors.text, border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}>
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
};

const TabButton = ({ active, onClick, children, color }) => (
  <button
    onClick={onClick}
    style={{
      padding: '12px 24px',
      background: active ? color : 'transparent',
      color: active ? colors.text : colors.textSecondary,
      border: `1px solid ${active ? color : colors.surfaceLight}`,
      borderRadius: '8px 8px 0 0',
      cursor: 'pointer',
      fontWeight: active ? 600 : 400,
      fontSize: '14px',
      transition: 'all 0.2s',
    }}
  >
    {children}
  </button>
);

const StatusBadge = ({ status }) => {
  const statusColors = {
    'Pending': colors.warning,
    'In Progress': colors.accent,
    'Completed': colors.success,
  };
  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      background: `${statusColors[status] || colors.textSecondary}20`,
      color: statusColors[status] || colors.textSecondary,
    }}>
      {status}
    </span>
  );
};

// ============================================================================
// MAIN APPLICATION
// ============================================================================

const MainApp = ({ msalUser, getAccessToken }) => {
  const { instance } = useMsal();
  const [activeTab, setActiveTab] = useState('offerLetters');
  const [requisitions, setRequisitions] = useState([]);
  const [onboardingRecords, setOnboardingRecords] = useState([]);
  const [offboardingRecords, setOffboardingRecords] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);
  const [selectedOffboarding, setSelectedOffboarding] = useState(null);
  
  // Offer letter form data
  const [offerData, setOfferData] = useState({
    requisitionId: '',
    firstName: '',
    lastName: '',
    personalEmail: '',
    jobTitle: '',
    department: '',
    workLocation: '111 S. Wood Ave Unit #100 Iselin, NJ 08830',
    roleType: 'Remote',
    startDate: '',
    offerDate: new Date().toISOString().split('T')[0],
    signByDate: '',
    baseSalary: '',
    bonusPercentage: '',
    bonusYear: new Date().getFullYear().toString(),
    stockOptions: '',
    vacationWeeks: '3',
    otherCompBonus: '',
    hiringManagerName: '',
    hiringManagerTitle: '',
  });

  // Onboarding form data
  const [onboardingData, setOnboardingData] = useState({
    linkedRequisitionId: '',
    firstName: '',
    lastName: '',
    personalEmail: '',
    jobTitle: '',
    department: '',
    hiringManager: '',
    hiringManagerEmail: '',
    startDate: '',
    workLocation: '',
    roleType: 'Remote',
    // Address fields
    streetAddress: '',
    zipCity: '',
    country: '',
    // Employee type
    employeeType: 'Employee',
    // Software access - individual fields matching SharePoint columns
    needsDocusign: false,
    needsNetsuite: false,
    needsAdobePro: false,
    sharePointAccess: '',
    shareFileAccess: '',
    notes: '',
  });

  // Offboarding form data
  const [offboardingData, setOffboardingData] = useState({
    employeeEmail: '',
    employeeName: '',
    isUrgent: false,
    lastDay: '',
    removeAccessDate: '',
    equipmentReturnLocation: 'us',
    equipmentReturnOther: '',
    mailboxOptions: [],
    oooMessage: '',
    sharedMailboxUsers: '',
    otherMailboxNotes: '',
    notes: '',
  });

  // Settings - notification recipients
  const [notificationRecipients, setNotificationRecipients] = useState(DEFAULT_RECIPIENTS);
  const [newRecipient, setNewRecipient] = useState({ name: '', email: '' });

  const departments = ['Accounting', 'Commercial Operations', 'Corporate: Admin', 'Corporate: Executive', 'Distribution', 'Human Resources', 'Marketing', 'Marketing: Market Access', 'Medical', 'R&D: Quality Assurance', 'R&D: Regulatory', 'Sales', 'Engineering', 'Clinical Operations', 'Quality', 'Finance', 'IT', 'Legal'];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      const [reqs, onboarding, offboarding, settingsData, adminStatus] = await Promise.all([
        loadApprovedRequisitions(token),
        loadOnboardingRecords(token),
        loadOffboardingRecords(token),
        loadSettings(token),
        checkIsAdmin(token, msalUser.email),
      ]);
      setRequisitions(reqs);
      setOnboardingRecords(onboarding);
      setOffboardingRecords(offboarding);
      setSettings(settingsData);
      setIsAdmin(adminStatus);
      
      // Load notification recipients from settings
      if (settingsData.NotificationRecipients?.value) {
        setNotificationRecipients(settingsData.NotificationRecipients.value);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, msalUser.email]);

  useEffect(() => { loadData(); }, [loadData]);

  // Derive hiring manager email from name
  const deriveEmail = (name) => {
    if (!name) return '';
    const parts = name.toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]}${parts[parts.length - 1]}@outlooktherapeutics.com`;
    }
    return '';
  };

  const handleRequisitionSelect = (reqId) => {
    const req = requisitions.find(r => r.id === reqId);
    const onboarding = onboardingRecords.find(o => o.linkedRequisitionId === reqId);
    
    setSelectedRequisition(req);
    setOfferData(prev => ({
      ...prev,
      requisitionId: reqId,
      firstName: onboarding?.firstName || '',
      lastName: onboarding?.lastName || '',
      personalEmail: onboarding?.personalEmail || '',
      jobTitle: req?.positionTitle || onboarding?.jobTitle || '',
      department: req?.department || onboarding?.department || '',
      workLocation: onboarding?.workLocation || '111 S. Wood Ave Unit #100 Iselin, NJ 08830',
      roleType: req?.roleType || 'Remote',
      startDate: onboarding?.startDate?.split('T')[0] || req?.projectedStartDate?.split('T')[0] || '',
      // Pre-fill from negotiated terms
      baseSalary: req?.negotiatedSalary?.replace(/[^0-9]/g, '') || '',
      stockOptions: req?.negotiatedStockOptions?.replace(/[^0-9]/g, '') || '',
      bonusPercentage: req?.negotiatedBonus?.replace(/[^0-9]/g, '') || '',
      vacationWeeks: req?.negotiatedVacationDays ? Math.round(parseInt(req.negotiatedVacationDays) / 5) : '3',
      otherCompBonus: req?.negotiatedOtherCompBonus || '',
      hiringManagerName: req?.requestorName || '',
    }));
  };

  const handleOfferRequisitionSelectForOnboarding = (reqId) => {
    const req = requisitions.find(r => r.id === reqId);
    if (req) {
      const managerEmail = deriveEmail(req.requestorName);
      setOnboardingData(prev => ({
        ...prev,
        linkedRequisitionId: reqId,
        jobTitle: req.positionTitle || '',
        department: req.department || '',
        hiringManager: req.requestorName || '', // For display/notifications only
        hiringManagerEmail: managerEmail,
        startDate: req.projectedStartDate?.split('T')[0] || '',
        roleType: req.roleType || 'Remote',
      }));
    }
  };

  const prefillFromOfferLetter = () => {
    // Pre-fill onboarding from current offer letter data
    setOnboardingData(prev => ({
      ...prev,
      linkedRequisitionId: offerData.requisitionId,
      firstName: offerData.firstName,
      lastName: offerData.lastName,
      personalEmail: offerData.personalEmail,
      jobTitle: offerData.jobTitle,
      department: offerData.department,
      hiringManager: offerData.hiringManagerName,
      hiringManagerEmail: deriveEmail(offerData.hiringManagerName),
      startDate: offerData.startDate,
      workLocation: offerData.workLocation,
      roleType: offerData.roleType,
    }));
    setActiveTab('onboarding');
  };

  const handleGenerateAndDownload = async () => {
    if (!offerData.firstName || !offerData.lastName || !offerData.baseSalary) {
      alert('Please fill in all required fields (Name, Salary)');
      return;
    }
    
    setSaving(true);
    try {
      const blob = await generateOfferLetter(offerData);
      const fileName = `Offer_Letter_${offerData.lastName}_${offerData.firstName}_${new Date().toISOString().split('T')[0]}.docx`;
      saveAs(blob, fileName);
    } catch (err) {
      console.error('Error generating offer letter:', err);
      alert('Failed to generate offer letter');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAndSave = async () => {
    if (!offerData.firstName || !offerData.lastName || !offerData.baseSalary || !offerData.requisitionId) {
      alert('Please select a requisition and fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const token = await getAccessToken();
      const blob = await generateOfferLetter(offerData);
      const buffer = await blob.arrayBuffer();
      const fileName = `Offer_Letter_${offerData.lastName}_${offerData.firstName}_${new Date().toISOString().split('T')[0]}.docx`;
      
      await uploadOfferLetter(token, offerData.requisitionId, fileName, buffer);
      saveAs(blob, fileName);
      
      alert('Offer letter generated and saved to SharePoint!');
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save offer letter: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitOnboarding = async () => {
    if (!onboardingData.firstName || !onboardingData.lastName || !onboardingData.jobTitle || !onboardingData.startDate || !onboardingData.hiringManagerEmail) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = await getAccessToken();
      const requestId = await createOnboardingRecord(token, onboardingData, msalUser);
      
      // Build recipient list (active recipients + hiring manager)
      const recipients = notificationRecipients.filter(r => r.active);
      if (onboardingData.hiringManagerEmail) {
        recipients.push({ name: onboardingData.hiringManager || onboardingData.hiringManagerEmail, email: onboardingData.hiringManagerEmail });
      }

      // Send notifications
      const emailBody = buildOnboardingEmail(onboardingData, requestId);
      await sendEmailNotification(token, recipients, `🚀 New Onboarding Request: ${requestId} - ${onboardingData.firstName} ${onboardingData.lastName}`, emailBody);
      await postToTeamsChannel(token, `New Onboarding Request: ${requestId}`, emailBody);

      alert(`Onboarding request ${requestId} submitted successfully!`);
      
      // Reset form and reload data
      setOnboardingData({
        linkedRequisitionId: '', firstName: '', lastName: '', personalEmail: '',
        jobTitle: '', department: '', hiringManager: '', hiringManagerEmail: '',
        startDate: '', workLocation: '', roleType: 'Remote',
        streetAddress: '', zipCity: '', country: '', employeeType: 'Employee',
        needsDocusign: false, needsNetsuite: false, needsAdobePro: false,
        sharePointAccess: '', shareFileAccess: '', notes: '',
      });
      await loadData();
    } catch (err) {
      console.error('Error submitting onboarding:', err);
      alert('Failed to submit onboarding request: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitOffboarding = async () => {
    if (!offboardingData.employeeEmail || !offboardingData.lastDay || !offboardingData.removeAccessDate) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = await getAccessToken();
      const requestId = await createOffboardingRecord(token, offboardingData, msalUser);
      
      // Build recipient list (active recipients)
      const recipients = notificationRecipients.filter(r => r.active);

      // Send notifications
      const emailBody = buildOffboardingEmail(offboardingData, requestId);
      const urgentPrefix = offboardingData.isUrgent ? '🚨 URGENT: ' : '';
      await sendEmailNotification(token, recipients, `${urgentPrefix}👋 Offboarding Request: ${requestId} - ${offboardingData.employeeEmail}`, emailBody);
      await postToTeamsChannel(token, `${urgentPrefix}Offboarding Request: ${requestId}`, emailBody);

      alert(`Offboarding request ${requestId} submitted successfully!`);
      
      // Reset form and reload data
      setOffboardingData({
        employeeEmail: '', employeeName: '', isUrgent: false, lastDay: '', removeAccessDate: '',
        equipmentReturnLocation: 'us', equipmentReturnOther: '', mailboxOptions: [],
        oooMessage: '', sharedMailboxUsers: '', otherMailboxNotes: '', notes: '',
      });
      await loadData();
    } catch (err) {
      console.error('Error submitting offboarding:', err);
      alert('Failed to submit offboarding request: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = await getAccessToken();
      await saveSettings(token, 'NotificationRecipients', notificationRecipients, msalUser);
      alert('Settings saved successfully!');
    } catch (err) {
      console.error('Error saving settings:', err);
      alert('Failed to save settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    if (!newRecipient.name || !newRecipient.email) {
      alert('Please enter both name and email');
      return;
    }
    setNotificationRecipients([...notificationRecipients, { ...newRecipient, active: true }]);
    setNewRecipient({ name: '', email: '' });
  };

  const removeRecipient = (index) => {
    setNotificationRecipients(notificationRecipients.filter((_, i) => i !== index));
  };

  const toggleRecipient = (index) => {
    setNotificationRecipients(notificationRecipients.map((r, i) => 
      i === index ? { ...r, active: !r.active } : r
    ));
  };

  const handleMailboxOptionToggle = (optionId) => {
    setOffboardingData(prev => ({
      ...prev,
      mailboxOptions: prev.mailboxOptions.includes(optionId)
        ? prev.mailboxOptions.filter(o => o !== optionId)
        : [...prev.mailboxOptions, optionId]
    }));
  };

  const handleLogout = () => instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });

  const inputStyle = { padding: '10px 14px', borderRadius: '6px', border: `1px solid ${colors.surfaceLight}`, background: colors.background, color: colors.text, fontSize: '14px', width: '100%', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '13px', color: colors.textSecondary, display: 'block', marginBottom: '6px' };

  if (loading) return <LoadingScreen message="Loading HR Dashboard..." />;

  return (
    <div style={{ minHeight: '100vh', background: colors.background, fontFamily: "'Segoe UI', sans-serif", color: colors.text }}>
      {/* Header */}
      <header style={{ background: colors.surface, borderBottom: `1px solid ${colors.surfaceLight}`, padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', background: colors.hr, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 700 }}>HR</div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>CONNECT HR</div>
            <div style={{ fontSize: '11px', color: colors.textSecondary }}>HR Management Dashboard</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ color: colors.textSecondary, fontSize: '14px' }}>{msalUser.name}</span>
          {isAdmin && <span style={{ padding: '4px 8px', background: colors.warning, color: '#000', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>ADMIN</span>}
          <button onClick={handleLogout} style={{ padding: '8px 16px', background: 'transparent', color: colors.textSecondary, border: `1px solid ${colors.surfaceLight}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>Sign Out</button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div style={{ padding: '24px 24px 0', maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: '8px', borderBottom: `1px solid ${colors.surfaceLight}` }}>
          <TabButton active={activeTab === 'offerLetters'} onClick={() => setActiveTab('offerLetters')} color={colors.hr}>📄 Offer Letters</TabButton>
          <TabButton active={activeTab === 'onboarding'} onClick={() => setActiveTab('onboarding')} color={colors.onboarding}>🚀 Onboarding</TabButton>
          <TabButton active={activeTab === 'offboarding'} onClick={() => setActiveTab('offboarding')} color={colors.offboarding}>👋 Offboarding</TabButton>
          {isAdmin && <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} color={colors.warning}>⚙️ Settings</TabButton>}
        </div>
      </div>

      {/* Main Content */}
      <main style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* ===== OFFER LETTERS TAB ===== */}
        {activeTab === 'offerLetters' && (
          <>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.hr}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.hr }}>{requisitions.length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Approved Requisitions</div>
              </div>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.success}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.success }}>{requisitions.filter(r => r.status === 'Verbal Offer').length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Verbal Offers</div>
              </div>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.accent}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.accent }}>{requisitions.filter(r => r.status === 'Offer Letter in Progress').length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Offer Letters in Progress</div>
              </div>
            </div>

            <div style={{ background: colors.surface, borderRadius: '10px', padding: '24px' }}>
              <h2 style={{ fontSize: '18px', marginBottom: '20px', color: colors.hr }}>Generate Offer Letter</h2>
              
              {/* Requisition Selection */}
              <div style={{ marginBottom: '24px', padding: '16px', background: colors.background, borderRadius: '8px' }}>
                <label style={labelStyle}>Select Requisition *</label>
                <select value={offerData.requisitionId} onChange={(e) => handleRequisitionSelect(e.target.value)} style={inputStyle}>
                  <option value="">-- Select a requisition --</option>
                  {requisitions.map(req => (
                    <option key={req.id} value={req.id}>
                      {req.id} - {req.positionTitle} ({req.department}) - {req.status}
                    </option>
                  ))}
                </select>
                {selectedRequisition && (
                  <div style={{ marginTop: '12px', padding: '12px', background: colors.surface, borderRadius: '6px', fontSize: '13px' }}>
                    <div><strong>Position:</strong> {selectedRequisition.positionTitle}</div>
                    <div><strong>Department:</strong> {selectedRequisition.department}</div>
                    <div><strong>Status:</strong> {selectedRequisition.status}</div>
                    {selectedRequisition.negotiatedSalary && <div><strong>Negotiated Salary:</strong> {selectedRequisition.negotiatedSalary}</div>}
                  </div>
                )}
              </div>

              {/* Candidate Information */}
              <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Candidate Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div><label style={labelStyle}>First Name *</label><input value={offerData.firstName} onChange={e => setOfferData({...offerData, firstName: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Last Name *</label><input value={offerData.lastName} onChange={e => setOfferData({...offerData, lastName: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Personal Email</label><input value={offerData.personalEmail} onChange={e => setOfferData({...offerData, personalEmail: e.target.value})} style={inputStyle} /></div>
              </div>

              {/* Position Details */}
              <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Position Details</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div><label style={labelStyle}>Job Title *</label><input value={offerData.jobTitle} onChange={e => setOfferData({...offerData, jobTitle: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Department</label><input value={offerData.department} onChange={e => setOfferData({...offerData, department: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Role Type</label><select value={offerData.roleType} onChange={e => setOfferData({...offerData, roleType: e.target.value})} style={inputStyle}><option value="Remote">Remote</option><option value="Hybrid">Hybrid</option><option value="Onsite">Onsite</option></select></div>
                <div><label style={labelStyle}>Work Location</label><input value={offerData.workLocation} onChange={e => setOfferData({...offerData, workLocation: e.target.value})} style={inputStyle} /></div>
              </div>

              {/* Dates */}
              <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Dates</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div><label style={labelStyle}>Offer Date *</label><input type="date" value={offerData.offerDate} onChange={e => setOfferData({...offerData, offerDate: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Start Date *</label><input type="date" value={offerData.startDate} onChange={e => setOfferData({...offerData, startDate: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Sign By Date</label><input type="date" value={offerData.signByDate} onChange={e => setOfferData({...offerData, signByDate: e.target.value})} style={inputStyle} /></div>
              </div>

              {/* Compensation */}
              <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Compensation</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div><label style={labelStyle}>Base Salary (Annual) *</label><input type="number" value={offerData.baseSalary} onChange={e => setOfferData({...offerData, baseSalary: e.target.value})} placeholder="e.g., 120000" style={inputStyle} /></div>
                <div><label style={labelStyle}>Bonus % of Base</label><input type="number" value={offerData.bonusPercentage} onChange={e => setOfferData({...offerData, bonusPercentage: e.target.value})} placeholder="e.g., 15" style={inputStyle} /></div>
                <div><label style={labelStyle}>Bonus Year</label><input value={offerData.bonusYear} onChange={e => setOfferData({...offerData, bonusYear: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Stock Options (Shares)</label><input type="number" value={offerData.stockOptions} onChange={e => setOfferData({...offerData, stockOptions: e.target.value})} placeholder="e.g., 50000" style={inputStyle} /></div>
                <div><label style={labelStyle}>Vacation (Weeks)</label><input type="number" value={offerData.vacationWeeks} onChange={e => setOfferData({...offerData, vacationWeeks: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Other Comp/Bonus</label><input value={offerData.otherCompBonus} onChange={e => setOfferData({...offerData, otherCompBonus: e.target.value})} placeholder="e.g., Signing bonus $5K" style={inputStyle} /></div>
              </div>

              {/* Hiring Manager */}
              <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Hiring Manager (Signatory)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div><label style={labelStyle}>Hiring Manager Name *</label><input value={offerData.hiringManagerName} onChange={e => setOfferData({...offerData, hiringManagerName: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>Hiring Manager Title *</label><input value={offerData.hiringManagerTitle} onChange={e => setOfferData({...offerData, hiringManagerTitle: e.target.value})} placeholder="e.g., Chief Executive Officer" style={inputStyle} /></div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: `1px solid ${colors.surfaceLight}` }}>
                <button onClick={prefillFromOfferLetter} disabled={!offerData.firstName || !offerData.lastName} style={{ padding: '12px 24px', background: colors.onboarding, color: colors.text, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, opacity: (!offerData.firstName || !offerData.lastName) ? 0.5 : 1 }}>
                  → Start Onboarding
                </button>
                <button onClick={handleGenerateAndDownload} disabled={saving} style={{ padding: '12px 24px', background: colors.accent, color: colors.text, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Generating...' : 'Download Offer Letter'}
                </button>
                <button onClick={handleGenerateAndSave} disabled={saving || !offerData.requisitionId} style={{ padding: '12px 24px', background: colors.hr, color: colors.text, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, opacity: (saving || !offerData.requisitionId) ? 0.7 : 1 }}>
                  {saving ? 'Saving...' : 'Download & Save to SharePoint'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ===== ONBOARDING TAB ===== */}
        {activeTab === 'onboarding' && (
          <>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.onboarding}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.onboarding }}>{onboardingRecords.length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Total Onboarding Requests</div>
              </div>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.warning}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.warning }}>{onboardingRecords.filter(r => r.status === 'Pending').length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Pending</div>
              </div>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.success}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.success }}>{onboardingRecords.filter(r => r.status === 'Completed').length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Completed</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* Onboarding Form */}
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '24px' }}>
                <h2 style={{ fontSize: '18px', marginBottom: '20px', color: colors.onboarding }}>🚀 New Onboarding Request</h2>
                
                {/* Link to Requisition (optional) */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Link to Requisition (Optional)</label>
                  <select value={onboardingData.linkedRequisitionId} onChange={(e) => handleOfferRequisitionSelectForOnboarding(e.target.value)} style={inputStyle}>
                    <option value="">-- Select to pre-fill --</option>
                    {requisitions.map(req => (
                      <option key={req.id} value={req.id}>{req.id} - {req.positionTitle}</option>
                    ))}
                  </select>
                </div>

                {/* Employee Information */}
                <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Employee Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div><label style={labelStyle}>First Name *</label><input value={onboardingData.firstName} onChange={e => setOnboardingData({...onboardingData, firstName: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Last Name *</label><input value={onboardingData.lastName} onChange={e => setOnboardingData({...onboardingData, lastName: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Personal Email</label><input value={onboardingData.personalEmail} onChange={e => setOnboardingData({...onboardingData, personalEmail: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Employee Type *</label>
                    <select value={onboardingData.employeeType} onChange={e => setOnboardingData({...onboardingData, employeeType: e.target.value})} style={inputStyle}>
                      <option value="Employee">Employee</option>
                      <option value="Contractor">Contractor</option>
                      <option value="Part-Time">Part-Time</option>
                      <option value="Consultant">Consultant</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Address */}
                <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Address</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '20px' }}>
                  <div><label style={labelStyle}>Street Address</label><input value={onboardingData.streetAddress} onChange={e => setOnboardingData({...onboardingData, streetAddress: e.target.value})} placeholder="123 Main Street, Apt 4" style={inputStyle} /></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div><label style={labelStyle}>City / Zip Code</label><input value={onboardingData.zipCity} onChange={e => setOnboardingData({...onboardingData, zipCity: e.target.value})} placeholder="New York, NY 10001" style={inputStyle} /></div>
                    <div><label style={labelStyle}>Country</label><input value={onboardingData.country} onChange={e => setOnboardingData({...onboardingData, country: e.target.value})} placeholder="USA" style={inputStyle} /></div>
                  </div>
                </div>

                {/* Position Details */}
                <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Position Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div><label style={labelStyle}>Job Title *</label><input value={onboardingData.jobTitle} onChange={e => setOnboardingData({...onboardingData, jobTitle: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Department *</label><select value={onboardingData.department} onChange={e => setOnboardingData({...onboardingData, department: e.target.value})} style={inputStyle}><option value="">Select...</option>{departments.map(d => <option key={d} value={d}>{d}</option>)}</select></div>
                  <div><label style={labelStyle}>Start Date *</label><input type="date" value={onboardingData.startDate} onChange={e => setOnboardingData({...onboardingData, startDate: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Role Type</label><select value={onboardingData.roleType} onChange={e => setOnboardingData({...onboardingData, roleType: e.target.value})} style={inputStyle}><option value="Remote">Remote</option><option value="Hybrid">Hybrid</option><option value="Onsite">Onsite</option></select></div>
                  <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Work Location</label><input value={onboardingData.workLocation} onChange={e => setOnboardingData({...onboardingData, workLocation: e.target.value})} placeholder="e.g., 111 S. Wood Ave, Iselin, NJ" style={inputStyle} /></div>
                </div>

                {/* Hiring Manager */}
                <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Hiring Manager</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div><label style={labelStyle}>Hiring Manager Name</label><input value={onboardingData.hiringManager} onChange={e => setOnboardingData({...onboardingData, hiringManager: e.target.value, hiringManagerEmail: deriveEmail(e.target.value)})} placeholder="Auto-generates email" style={inputStyle} /></div>
                  <div><label style={labelStyle}>Hiring Manager Email *</label><input value={onboardingData.hiringManagerEmail} onChange={e => setOnboardingData({...onboardingData, hiringManagerEmail: e.target.value})} placeholder="manager@outlooktherapeutics.com" style={inputStyle} /></div>
                </div>

                {/* Software & Access */}
                <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Software & Access Requirements</h3>
                <div style={{ marginBottom: '20px', padding: '16px', background: colors.background, borderRadius: '8px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ ...labelStyle, marginBottom: '12px', display: 'block' }}>Software Licenses Needed:</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={onboardingData.needsDocusign} onChange={e => setOnboardingData({...onboardingData, needsDocusign: e.target.checked})} />
                        <span style={{ fontSize: '13px' }}>DocuSign</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={onboardingData.needsNetsuite} onChange={e => setOnboardingData({...onboardingData, needsNetsuite: e.target.checked})} />
                        <span style={{ fontSize: '13px' }}>NetSuite</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={onboardingData.needsAdobePro} onChange={e => setOnboardingData({...onboardingData, needsAdobePro: e.target.checked})} />
                        <span style={{ fontSize: '13px' }}>Adobe Pro</span>
                      </label>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label style={labelStyle}>SharePoint Site Access</label>
                      <textarea value={onboardingData.sharePointAccess} onChange={e => setOnboardingData({...onboardingData, sharePointAccess: e.target.value})} placeholder="List SharePoint sites/libraries..." style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
                    </div>
                    <div>
                      <label style={labelStyle}>ShareFile Access</label>
                      <textarea value={onboardingData.shareFileAccess} onChange={e => setOnboardingData({...onboardingData, shareFileAccess: e.target.value})} placeholder="List ShareFile folders..." style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} />
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Additional Notes</label>
                  <textarea value={onboardingData.notes} onChange={e => setOnboardingData({...onboardingData, notes: e.target.value})} placeholder="Any special requests or information..." style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
                </div>

                {/* Submit Button */}
                <button onClick={handleSubmitOnboarding} disabled={saving} style={{ width: '100%', padding: '14px', background: colors.onboarding, color: colors.text, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '15px', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Submitting...' : 'Submit Onboarding Request'}
                </button>
              </div>

              {/* Recent Onboarding Requests */}
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '24px' }}>
                <h2 style={{ fontSize: '18px', marginBottom: '20px', color: colors.textSecondary }}>Recent Onboarding Requests</h2>
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {onboardingRecords.length === 0 ? (
                    <p style={{ color: colors.textSecondary, textAlign: 'center', padding: '40px' }}>No onboarding requests yet</p>
                  ) : (
                    onboardingRecords.slice(0, 10).map(record => (
                      <div 
                        key={record.id} 
                        onClick={() => setSelectedOnboarding(record)}
                        style={{ padding: '16px', background: colors.background, borderRadius: '8px', marginBottom: '12px', borderLeft: `3px solid ${colors.onboarding}`, cursor: 'pointer', transition: 'transform 0.1s', ':hover': { transform: 'scale(1.01)' } }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{record.firstName} {record.lastName}</div>
                            <div style={{ fontSize: '12px', color: colors.textSecondary }}>{record.onboardingId}</div>
                          </div>
                          <StatusBadge status={record.status} />
                        </div>
                        <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                          <div>{record.jobTitle} • {record.department}</div>
                          <div>Start: {record.startDate ? new Date(record.startDate).toLocaleDateString() : 'N/A'}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Onboarding Detail Modal */}
            {selectedOnboarding && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSelectedOnboarding(null)}>
                <div style={{ background: colors.surface, borderRadius: '12px', padding: '32px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div>
                      <h2 style={{ fontSize: '20px', margin: 0, color: colors.onboarding }}>{selectedOnboarding.onboardingId}</h2>
                      <p style={{ margin: '4px 0 0', color: colors.textSecondary }}>{selectedOnboarding.firstName} {selectedOnboarding.lastName}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <StatusBadge status={selectedOnboarding.status} />
                      <button onClick={() => setSelectedOnboarding(null)} style={{ background: 'transparent', border: 'none', color: colors.textSecondary, fontSize: '24px', cursor: 'pointer' }}>×</button>
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Employee Type</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.employeeType || 'Employee'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Job Title</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.jobTitle}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Department</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.department}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Start Date</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.startDate ? new Date(selectedOnboarding.startDate).toLocaleDateString() : 'N/A'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Manager Email</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.hiringManagerEmail || 'N/A'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Role Type</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.roleType}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Personal Email</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.personalEmail || 'N/A'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Work Location</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.workLocation || 'N/A'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Address</div>
                      <div style={{ fontWeight: 500 }}>{[selectedOnboarding.streetAddress, selectedOnboarding.zipCity, selectedOnboarding.country].filter(Boolean).join(', ') || 'N/A'}</div>
                    </div>
                    
                    {/* Software Access */}
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Software Licenses</div>
                      <div style={{ fontWeight: 500 }}>
                        {[
                          selectedOnboarding.needsDocusign && 'DocuSign',
                          selectedOnboarding.needsNetsuite && 'NetSuite',
                          selectedOnboarding.needsAdobePro && 'Adobe Pro'
                        ].filter(Boolean).join(', ') || 'None requested'}
                      </div>
                    </div>
                    
                    {selectedOnboarding.sharePointAccess && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>SharePoint Access</div>
                        <div style={{ fontWeight: 500 }}>{selectedOnboarding.sharePointAccess}</div>
                      </div>
                    )}
                    
                    {selectedOnboarding.shareFileAccess && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>ShareFile Access</div>
                        <div style={{ fontWeight: 500 }}>{selectedOnboarding.shareFileAccess}</div>
                      </div>
                    )}
                    
                    {selectedOnboarding.notes && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Notes</div>
                        <div style={{ fontWeight: 500 }}>{selectedOnboarding.notes}</div>
                      </div>
                    )}
                    
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Submitted By</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.submittedBy || 'N/A'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Submitted At</div>
                      <div style={{ fontWeight: 500 }}>{selectedOnboarding.submittedAt ? new Date(selectedOnboarding.submittedAt).toLocaleString() : 'N/A'}</div>
                    </div>
                    
                    {selectedOnboarding.linkedRequisitionId && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Linked Requisition</div>
                        <div style={{ fontWeight: 500 }}>{selectedOnboarding.linkedRequisitionId}</div>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${colors.surfaceLight}`, display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={() => setSelectedOnboarding(null)} style={{ padding: '10px 24px', background: colors.surfaceLight, color: colors.text, border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Close</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== OFFBOARDING TAB ===== */}
        {activeTab === 'offboarding' && (
          <>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.offboarding}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.offboarding }}>{offboardingRecords.length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Total Offboarding Requests</div>
              </div>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.warning}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.warning }}>{offboardingRecords.filter(r => r.status === 'Pending').length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Pending</div>
              </div>
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '20px', borderLeft: `4px solid ${colors.error}` }}>
                <div style={{ fontSize: '32px', fontWeight: 700, color: colors.error }}>{offboardingRecords.filter(r => r.isUrgent && r.status === 'Pending').length}</div>
                <div style={{ fontSize: '13px', color: colors.textSecondary }}>Urgent</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* Offboarding Form */}
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '24px' }}>
                <h2 style={{ fontSize: '18px', marginBottom: '20px', color: colors.offboarding }}>👋 New Offboarding Request</h2>
                
                {/* Employee Email */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Who are you offboarding today (email address)? *</label>
                  <input type="email" value={offboardingData.employeeEmail} onChange={e => setOffboardingData({...offboardingData, employeeEmail: e.target.value})} placeholder="employee@outlooktherapeutics.com" style={inputStyle} />
                </div>

                {/* Urgent Toggle */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={offboardingData.isUrgent} onChange={e => setOffboardingData({...offboardingData, isUrgent: e.target.checked})} style={{ width: '18px', height: '18px' }} />
                    <span style={{ color: offboardingData.isUrgent ? colors.error : colors.textSecondary, fontWeight: offboardingData.isUrgent ? 600 : 400 }}>
                      🚨 Is this request urgent?
                    </span>
                  </label>
                </div>

                {/* Dates */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                  <div><label style={labelStyle}>Last day with OTLK? *</label><input type="date" value={offboardingData.lastDay} onChange={e => setOffboardingData({...offboardingData, lastDay: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Remove all system access as of *</label><input type="date" value={offboardingData.removeAccessDate} onChange={e => setOffboardingData({...offboardingData, removeAccessDate: e.target.value})} style={inputStyle} /></div>
                </div>

                {/* Equipment Return */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Equipment return location *</label>
                  {EQUIPMENT_RETURN_LOCATIONS.map(loc => (
                    <label key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', cursor: 'pointer' }}>
                      <input type="radio" name="equipmentReturn" checked={offboardingData.equipmentReturnLocation === loc.id} onChange={() => setOffboardingData({...offboardingData, equipmentReturnLocation: loc.id})} />
                      <span style={{ fontSize: '13px' }}>{loc.label}</span>
                    </label>
                  ))}
                  {offboardingData.equipmentReturnLocation === 'other' && (
                    <input value={offboardingData.equipmentReturnOther} onChange={e => setOffboardingData({...offboardingData, equipmentReturnOther: e.target.value})} placeholder="Specify other location..." style={{ ...inputStyle, marginTop: '8px' }} />
                  )}
                </div>

                {/* Mailbox Options */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Mailbox handling options *</label>
                  {MAILBOX_OPTIONS.map(opt => (
                    <label key={opt.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={offboardingData.mailboxOptions.includes(opt.id)} onChange={() => handleMailboxOptionToggle(opt.id)} style={{ marginTop: '2px' }} />
                      <span style={{ fontSize: '13px' }}>{opt.label}</span>
                    </label>
                  ))}
                  
                  {offboardingData.mailboxOptions.includes('ooo') && (
                    <textarea value={offboardingData.oooMessage} onChange={e => setOffboardingData({...offboardingData, oooMessage: e.target.value})} placeholder="Enter Out of Office message text..." style={{ ...inputStyle, marginTop: '8px', minHeight: '60px' }} />
                  )}
                  
                  {offboardingData.mailboxOptions.includes('shared') && (
                    <input value={offboardingData.sharedMailboxUsers} onChange={e => setOffboardingData({...offboardingData, sharedMailboxUsers: e.target.value})} placeholder="List users who need access (comma separated)" style={{ ...inputStyle, marginTop: '8px' }} />
                  )}
                  
                  {offboardingData.mailboxOptions.includes('other') && (
                    <input value={offboardingData.otherMailboxNotes} onChange={e => setOffboardingData({...offboardingData, otherMailboxNotes: e.target.value})} placeholder="Describe other mailbox handling..." style={{ ...inputStyle, marginTop: '8px' }} />
                  )}
                </div>

                {/* Additional Notes */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>Is there anything else we need to know?</label>
                  <textarea value={offboardingData.notes} onChange={e => setOffboardingData({...offboardingData, notes: e.target.value})} placeholder="Any additional information..." style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
                </div>

                {/* Submit Button */}
                <button onClick={handleSubmitOffboarding} disabled={saving} style={{ width: '100%', padding: '14px', background: colors.offboarding, color: colors.text, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '15px', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Submitting...' : 'Submit Offboarding Request'}
                </button>
              </div>

              {/* Recent Offboarding Requests */}
              <div style={{ background: colors.surface, borderRadius: '10px', padding: '24px' }}>
                <h2 style={{ fontSize: '18px', marginBottom: '20px', color: colors.textSecondary }}>Recent Offboarding Requests</h2>
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {offboardingRecords.length === 0 ? (
                    <p style={{ color: colors.textSecondary, textAlign: 'center', padding: '40px' }}>No offboarding requests yet</p>
                  ) : (
                    offboardingRecords.slice(0, 10).map(record => (
                      <div 
                        key={record.id} 
                        onClick={() => setSelectedOffboarding(record)}
                        style={{ padding: '16px', background: colors.background, borderRadius: '8px', marginBottom: '12px', borderLeft: `3px solid ${record.isUrgent ? colors.error : colors.offboarding}`, cursor: 'pointer' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{record.employeeEmail}</div>
                            <div style={{ fontSize: '12px', color: colors.textSecondary }}>{record.offboardingId}</div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {record.isUrgent && <span style={{ padding: '2px 6px', background: colors.error, color: '#fff', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>URGENT</span>}
                            <StatusBadge status={record.status} />
                          </div>
                        </div>
                        <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                          <div>Last Day: {record.lastDay ? new Date(record.lastDay).toLocaleDateString() : 'N/A'}</div>
                          <div>Access Removal: {record.removeAccessDate ? new Date(record.removeAccessDate).toLocaleDateString() : 'N/A'}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Offboarding Detail Modal */}
            {selectedOffboarding && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSelectedOffboarding(null)}>
                <div style={{ background: colors.surface, borderRadius: '12px', padding: '32px', maxWidth: '700px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div>
                      <h2 style={{ fontSize: '20px', margin: 0, color: colors.offboarding }}>{selectedOffboarding.offboardingId}</h2>
                      <p style={{ margin: '4px 0 0', color: colors.textSecondary }}>{selectedOffboarding.employeeEmail}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {selectedOffboarding.isUrgent && <span style={{ padding: '4px 10px', background: colors.error, color: '#fff', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>URGENT</span>}
                      <StatusBadge status={selectedOffboarding.status} />
                      <button onClick={() => setSelectedOffboarding(null)} style={{ background: 'transparent', border: 'none', color: colors.textSecondary, fontSize: '24px', cursor: 'pointer' }}>×</button>
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Employee Email</div>
                      <div style={{ fontWeight: 500 }}>{selectedOffboarding.employeeEmail}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Last Day</div>
                      <div style={{ fontWeight: 500 }}>{selectedOffboarding.lastDay ? new Date(selectedOffboarding.lastDay).toLocaleDateString() : 'N/A'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Remove Access Date</div>
                      <div style={{ fontWeight: 500 }}>{selectedOffboarding.removeAccessDate ? new Date(selectedOffboarding.removeAccessDate).toLocaleDateString() : 'N/A'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Equipment Return</div>
                      <div style={{ fontWeight: 500 }}>{EQUIPMENT_RETURN_LOCATIONS.find(l => l.id === selectedOffboarding.equipmentReturnLocation)?.label || selectedOffboarding.equipmentReturnLocation}</div>
                    </div>
                    
                    {selectedOffboarding.equipmentReturnOther && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Other Return Location</div>
                        <div style={{ fontWeight: 500 }}>{selectedOffboarding.equipmentReturnOther}</div>
                      </div>
                    )}
                    
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Mailbox Options</div>
                      <div style={{ fontWeight: 500 }}>{selectedOffboarding.mailboxOptions?.map(o => MAILBOX_OPTIONS.find(m => m.id === o)?.label || o).join(', ') || 'None'}</div>
                    </div>
                    
                    {selectedOffboarding.oooMessage && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Out of Office Message</div>
                        <div style={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>{selectedOffboarding.oooMessage}</div>
                      </div>
                    )}
                    
                    {selectedOffboarding.sharedMailboxUsers && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Shared Mailbox Users</div>
                        <div style={{ fontWeight: 500 }}>{selectedOffboarding.sharedMailboxUsers}</div>
                      </div>
                    )}
                    
                    {selectedOffboarding.otherMailboxNotes && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Other Mailbox Notes</div>
                        <div style={{ fontWeight: 500 }}>{selectedOffboarding.otherMailboxNotes}</div>
                      </div>
                    )}
                    
                    {selectedOffboarding.notes && (
                      <div style={{ padding: '12px', background: colors.background, borderRadius: '8px', gridColumn: 'span 2' }}>
                        <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Additional Notes</div>
                        <div style={{ fontWeight: 500 }}>{selectedOffboarding.notes}</div>
                      </div>
                    )}
                    
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Submitted By</div>
                      <div style={{ fontWeight: 500 }}>{selectedOffboarding.submittedBy || 'N/A'}</div>
                    </div>
                    <div style={{ padding: '12px', background: colors.background, borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>Submitted At</div>
                      <div style={{ fontWeight: 500 }}>{selectedOffboarding.submittedAt ? new Date(selectedOffboarding.submittedAt).toLocaleString() : 'N/A'}</div>
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: `1px solid ${colors.surfaceLight}`, display: 'flex', justifyContent: 'flex-end' }}>
                    <button onClick={() => setSelectedOffboarding(null)} style={{ padding: '10px 24px', background: colors.surfaceLight, color: colors.text, border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Close</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== SETTINGS TAB (Admin Only) ===== */}
        {activeTab === 'settings' && isAdmin && (
          <div style={{ background: colors.surface, borderRadius: '10px', padding: '24px', maxWidth: '800px' }}>
            <h2 style={{ fontSize: '18px', marginBottom: '20px', color: colors.warning }}>⚙️ Notification Settings</h2>
            <p style={{ color: colors.textSecondary, marginBottom: '24px' }}>
              Manage who receives email notifications when onboarding or offboarding requests are submitted.
            </p>

            {/* Current Recipients */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Current Recipients</h3>
              <div style={{ background: colors.background, borderRadius: '8px', padding: '16px' }}>
                {notificationRecipients.map((recipient, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: index < notificationRecipients.length - 1 ? `1px solid ${colors.surfaceLight}` : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <input type="checkbox" checked={recipient.active} onChange={() => toggleRecipient(index)} style={{ width: '16px', height: '16px' }} />
                      <div>
                        <div style={{ fontWeight: 500, opacity: recipient.active ? 1 : 0.5 }}>{recipient.name}</div>
                        <div style={{ fontSize: '12px', color: colors.textSecondary, opacity: recipient.active ? 1 : 0.5 }}>{recipient.email}</div>
                      </div>
                    </div>
                    <button onClick={() => removeRecipient(index)} style={{ padding: '6px 12px', background: 'transparent', color: colors.error, border: `1px solid ${colors.error}`, borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Remove</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Add New Recipient */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', color: colors.accent, marginBottom: '16px' }}>Add New Recipient</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
                <div><label style={labelStyle}>Name</label><input value={newRecipient.name} onChange={e => setNewRecipient({...newRecipient, name: e.target.value})} placeholder="John Doe" style={inputStyle} /></div>
                <div><label style={labelStyle}>Email</label><input value={newRecipient.email} onChange={e => setNewRecipient({...newRecipient, email: e.target.value})} placeholder="johndoe@outlooktherapeutics.com" style={inputStyle} /></div>
                <button onClick={addRecipient} style={{ padding: '10px 20px', background: colors.success, color: colors.text, border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 500 }}>Add</button>
              </div>
            </div>

            {/* Save Button */}
            <div style={{ paddingTop: '16px', borderTop: `1px solid ${colors.surfaceLight}` }}>
              <button onClick={handleSaveSettings} disabled={saving} style={{ padding: '12px 32px', background: colors.warning, color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </main>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ============================================================================
// APP ENTRY POINT
// ============================================================================

function App() {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const { user, loading, getAccessToken } = useCurrentUser();
  
  if (!isAuthenticated && inProgress === InteractionStatus.None) return <LoginScreen />;
  if (inProgress !== InteractionStatus.None || loading || !user) return <LoadingScreen message="Loading your profile..." />;
  
  return <MainApp msalUser={user} getAccessToken={getAccessToken} />;
}

export default App;
